import React, { useState } from "react";
import { api } from "../lib/api";
import { Container, SectionLabel } from "../components/ui-kit";
import PageHero from "../components/PageHero";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const TYPES = [
  ["general", "General Enquiry"], ["corporate", "Corporate Partnership"],
  ["csr", "CSR Enquiry"], ["partner", "Partner Enquiry"],
];

export default function Contact() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", country: "", organisation: "", enquiry_type: "general", message: "", consent: false });
  const [sent, setSent] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please complete the required fields.");
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Thank you. Your enquiry has been received.");
    } catch { toast.error("Could not send. Please try again."); }
  };

  return (
    <div>
      <PageHero label="Contact" title="Let's talk impact" subtitle="Corporate partnerships, CSR, partner enquiries or general questions — we'd love to hear from you." crumbs={[{ label: "Contact" }]} />
      <section className="bg-white">
        <Container className="grid gap-12 py-16 lg:grid-cols-[1fr_1.4fr]">
          <div>
            <SectionLabel>Helping Hands</SectionLabel>
            <h2 className="mt-4 font-display text-3xl font-bold text-emerald-900">Dubai, United Arab Emirates</h2>
            <div className="mt-8 space-y-4 text-slate-600">
              <div className="flex items-center gap-3"><MapPin className="h-5 w-5 text-gold" /> {settings?.address}</div>
              <div className="flex items-center gap-3"><Mail className="h-5 w-5 text-gold" /> {settings?.email}</div>
              <div className="flex items-center gap-3"><Phone className="h-5 w-5 text-gold" /> {settings?.phone}</div>
            </div>
          </div>

          {sent ? (
            <div className="flex flex-col items-center justify-center rounded-3xl border border-emerald-900/10 bg-sand p-12 text-center">
              <Send className="h-10 w-10 text-emerald-600" />
              <h3 className="mt-4 font-display text-2xl font-bold text-emerald-900">Enquiry received</h3>
              <p className="mt-2 text-sm text-slate-600">Thank you for reaching out. Our team will respond shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl border border-emerald-900/10 bg-sand p-8" data-testid="contact-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label>Name *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name" className="mt-1.5 bg-white" /></div>
                <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" className="mt-1.5 bg-white" /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5 bg-white" /></div>
                <div><Label>Country</Label><Input value={form.country} onChange={(e) => set("country", e.target.value)} className="mt-1.5 bg-white" /></div>
                <div><Label>Organisation</Label><Input value={form.organisation} onChange={(e) => set("organisation", e.target.value)} className="mt-1.5 bg-white" /></div>
                <div>
                  <Label>Enquiry Type</Label>
                  <Select value={form.enquiry_type} onValueChange={(v) => set("enquiry_type", v)}>
                    <SelectTrigger className="mt-1.5 bg-white" data-testid="contact-type"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-4"><Label>Message *</Label><Textarea rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message" className="mt-1.5 bg-white" /></div>
              <label className="mt-4 flex items-start gap-2.5 text-sm text-slate-600"><Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5" /> I consent to Helping Hands storing my enquiry to respond to me.</label>
              <button type="submit" data-testid="contact-submit" className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-emerald-900 px-6 py-3.5 text-sm font-bold text-white transition hover:bg-emerald-800"><Send className="h-4 w-4" /> Send Enquiry</button>
            </form>
          )}
        </Container>
      </section>
    </div>
  );
}
